import {
  ApplicationInsights,
  SeverityLevel,
} from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

let reactPlugin: ReactPlugin | null = null;
let appInsights: ApplicationInsights | null = null;

export enum NavType {
  /**
   * Navigation to another component or page within the app
   */
  Internal = "Internal",
  /**
   * Navigation to a link external from he app
   */
  External = "External",
}

export interface BaseEventProperties {
  /**
   * Name of the component that's tracking telemetry (e.g. "ExperimentDetails", "RolloutForm", etc.)
   */
  component: string;
  /**
   * Name of the extension that's tracking telemetry
   */
  extensionName?: string;
  /**
   * Name of the experiment that's tracking telemetry (if applicable)
   */
  experimentName?: string;
  /**
   * A unique identifier for the current page session, can be used to link all telemetry events within a page for a user
   */
  pageSessionId?: string;
  [key: string]: any;
}

export interface NavigationEventProperties {
  /**
   * Name of the component that's tracking telemetry, will override the base component if provided
   */
  component?: string;
  /**
   * The destination of the navigation, such as the name of the page or component navigated to, or the URL if external
   */
  destination: string;
  /**
   * Internal | External - whether the navigation is within the app or to an external link
   */
  navType: NavType;
  /**
   * The function or user action that triggered the navigation event
   */
  function?: string;
  /**
   * Additional properties to include with the event
   */
  properties?: { [key: string]: any };
}

export interface OperationEventProperties {
  /**
   * The name of the operation being tracked
   */
  operation: string;
  /**
   *  Name of the experiment that's tracking telemetry, will override the base experimentName if provided
   */
  experimentName?: string;
  /**
   * The status of the operation (e.g. Started, Succeeded, Failed, etc.)
   */
  status?: OperationStatus;
  /**
   * The function or user action that triggered the operation event
   */
  function?: string;
  /**
   * Additional properties to include with the event
   */
  properties?: { [key: string]: any };
}

export enum OperationStatus {
  Started = "Started",
  Succeeded = "Succeeded",
  Completed = "Completed",
  Failed = "Failed",
  Canceled = "Canceled",
}

export interface ExceptionEventProperties {
  /**
   * The function or user action that triggered the exception
   */
  function?: string;
  /**
   * Additional properties to include with the event
   */
  properties?: { [key: string]: any };
}

/**
 * Initialize Application Insights
 * @param connectionString - Application Insights connection string
 */
export const initializeAppInsights = (
  connectionString?: string,
): ReactPlugin => {
  if (!connectionString) {
    console.warn(
      "Application Insights connection string not provided. Telemetry will not be collected.",
    );
    // Return a dummy plugin if no connection string is provided
    reactPlugin = new ReactPlugin();
    return reactPlugin;
  }

  if (reactPlugin && appInsights) {
    return reactPlugin;
  }

  reactPlugin = new ReactPlugin();

  appInsights = new ApplicationInsights({
    config: {
      connectionString: connectionString,
      enableAutoRouteTracking: true,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: {},
      },
      disableFetchTracking: false,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
    },
  });

  appInsights.loadAppInsights();
  appInsights.trackPageView();

  return reactPlugin;
};

/**
 * Get the Application Insights instance
 */
export const getAppInsights = (): ApplicationInsights | null => {
  return appInsights;
};

/**
 * Get the React Plugin instance
 */
export const getReactPlugin = (): ReactPlugin | null => {
  return reactPlugin;
};

/**
 * Track a custom event
 */
export const trackCustomEvent = (
  name: string,
  properties?: BaseEventProperties,
) => {
  if (appInsights) {
    appInsights.trackEvent(
      { name },
      {
        host: process.env.HOST_NAME || window.location.hostname,
        ...properties,
      },
    );
  }
};

/**
 * Track an exception
 */
export const trackException = (
  error: Error | string,
  severityLevel?: SeverityLevel,
  properties?: BaseEventProperties,
) => {
  if (appInsights) {
    const errorObject = typeof error === "string" ? new Error(error) : error;
    appInsights.trackException(
      { exception: errorObject, severityLevel },
      {
        host: process.env.HOST_NAME || window.location.hostname,
        ...properties,
      },
    );
  }
};

/**
 * Tracks when a user navigates from one page/component to another
 */
export const baseTrackNavigationEvent = (
  properties: NavigationEventProperties & BaseEventProperties,
) => {
  trackCustomEvent(`Navigation`, {
    ...properties,
  });
};

/**
 * Tracks an operation event, such as a button click side affect of an action
 */
export const baseTrackOperationEvent = (
  properties: OperationEventProperties & BaseEventProperties,
) => {
  trackCustomEvent(`Operation`, {
    ...properties,
  });
};

export const baseTrackExceptionEvent = (
  error: Error | string,
  severityLevel: SeverityLevel = SeverityLevel.Error,
  properties: ExceptionEventProperties & BaseEventProperties,
) => {
  trackException(error, severityLevel, {
    ...properties,
  });
};

/**
 * Track a metric
 */
export const trackMetric = (
  name: string,
  average: number,
  properties?: { [key: string]: any },
) => {
  if (appInsights) {
    appInsights.trackMetric({ name, average }, properties || {});
  }
};

/**
 * Creates a tracker for an async operation that emits correlated
 * Started / Succeeded / Failed events via baseTrackOperationEvent.
 */
export const createBaseOperationTracker = (
  operation: string,
  functionName: string,
  baseProperties: BaseEventProperties,
) => {
  const pageSessionId = crypto.randomUUID();

  const emit = (status: OperationStatus, extra?: { [key: string]: any }) => {
    baseTrackOperationEvent({
      operation,
      status,
      pageSessionId,
      function: functionName,
      ...baseProperties,
      properties: extra,
    });
  };

  const emitError = (error: Error | string, extra?: { [key: string]: any }) => {
    const errorMessage = typeof error === "string" ? error : error.message;
    baseTrackOperationEvent({
      operation,
      status: OperationStatus.Failed,
      pageSessionId,
      function: functionName,
      ...baseProperties,
      properties: extra,
    });
    baseTrackExceptionEvent(errorMessage, SeverityLevel.Error, {
      function: functionName,
      ...baseProperties,
      properties: extra,
    });
  };

  return {
    pageSessionId,
    start: (properties?: { [key: string]: any }) => {
      emit(OperationStatus.Started, properties);
    },
    step: (stepName: string, properties?: { [key: string]: any }) => {
      emit(OperationStatus.Succeeded, { ...properties, step: stepName });
    },
    succeed: (properties?: { [key: string]: any }) => {
      emit(OperationStatus.Completed, properties);
    },
    fail: (error?: string, properties?: { [key: string]: any }) => {
      emitError(error || "Operation failed", properties);
    },
  };
};
